import type {
  BaseRecord,
  CustomParams,
  CustomResponse,
  DataProvider,
  GetListParams,
  GetListResponse
} from '@refinedev/core';

import type { FilterOptionMap } from '@/utils/listResponse';
import { getFilterOptionMapFromMetaData } from '@/utils/listResponse';
import type { ListMetaData } from '@/utils/types';

import type { DataProviderEnhancer } from '../types';

export const withFilterEnhancer: DataProviderEnhancer = (
  provider: DataProvider
) => {
  return {
    ...provider,

    getList: async <TData extends BaseRecord = BaseRecord>(
      params: GetListParams
    ): Promise<GetListResponse<TData>> => {
      const response = await provider.getList<TData>(params);
      injectFilterMap(response.data);
      return response;
    },

    custom: async <
      TData extends BaseRecord = BaseRecord,
      TQuery = unknown,
      TPayload = unknown
    >(
      params: CustomParams<TQuery, TPayload>
    ): Promise<CustomResponse<TData>> => {
      const response = await provider.custom!<TData>(params);
      injectFilterMap(response.data);
      return response;
    }
  };
};

function isListData(data: unknown): data is {
  metadata: ListMetaData;
  items: unknown[];
  filter?: FilterOptionMap;
} {
  return (
    typeof data === 'object' &&
    data !== null &&
    'metadata' in data &&
    'items' in data &&
    Array.isArray((data as { items: unknown[] }).items)
  );
}

function injectFilterMap(data: unknown): void {
  if (!isListData(data)) return;

  try {
    const filterMap = getFilterOptionMapFromMetaData(data.metadata);
    data.filter = filterMap;
  } catch (error) {
    console.warn('Failed to inject filter metadata', error);
  }
}
